import { useState, useEffect } from 'react'
import {
  Container,
  Typography,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material'
import { trpc } from '../utils'
import type { StoredDocument } from '../../server/services/documentStorageService'
import axios from 'axios'
import { User } from '../../types'
import {
  DocumentsTable,
  BulkActionsToolbar,
  DocumentsDeleteDialog,
  DocumentsSummary,
} from '../components'
import { CONTAINER_MAX_WIDTH } from '../constants'

export function DocumentsManagement() {
  // State
  const [documents, setDocuments] = useState<StoredDocument[]>([])
  const [members, setMembers] = useState<User[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedDocuments, setSelectedDocuments] = useState<Set<string>>(
    new Set(),
  )
  const [updatingDocuments, setUpdatingDocuments] = useState<Set<string>>(
    new Set(),
  )
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [documentToDelete, setDocumentToDelete] = useState<string | null>(null)
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false)

  // Data loading
  useEffect(() => {
    loadData()
    fetchDocuments()
  }, [])

  const loadData = async () => {
    try {
      const { data } = await axios.get('/api-active-team')
      setMembers(data.data.members)
    } catch (error) {
      console.error('Error loading team data:', error)
    }
  }

  const fetchDocuments = async () => {
    try {
      setIsLoading(true)
      setError(null)
      const result = await trpc.getAllTeamDocuments.query()
      if (result.success) {
        setDocuments(result.documents)
      } else {
        setError('Evraklar yüklenemedi')
      }
    } catch (err: any) {
      setError(err.message || 'Bir hata oluştu')
    } finally {
      setIsLoading(false)
    }
  }

  // Document actions
  const handleDeleteDocument = async (documentId: string) => {
    try {
      const result = await trpc.deleteDocument.mutate({ documentId })
      if (result.success) {
        await fetchDocuments()
        setDeleteDialogOpen(false)
        setDocumentToDelete(null)
      }
    } catch (error) {
      console.error('Error deleting document:', error)
    }
  }

  const handleBulkDelete = async () => {
    try {
      for (const documentId of selectedDocuments) {
        await trpc.deleteDocument.mutate({ documentId })
      }
      await fetchDocuments()
      setSelectedDocuments(new Set())
      setBulkDeleteDialogOpen(false)
    } catch (error) {
      console.error('Error bulk deleting documents:', error)
    }
  }

  const handleBulkInvertedUpdate = async (inverted: boolean) => {
    try {
      for (const documentId of selectedDocuments) {
        const document = documents.find((doc) => doc.id === documentId)
        if (document) {
          await trpc.updateDocumentInverted.mutate({
            userId: document.metadata.userId,
            documentId,
            inverted,
          })
        }
      }
      await fetchDocuments()
      setSelectedDocuments(new Set())
    } catch (error) {
      console.error('Error bulk updating documents:', error)
    }
  }

  const handleInvertedToggle = async (
    userId: string,
    documentId: string,
    currentInverted: boolean,
  ) => {
    try {
      setUpdatingDocuments((prev) => new Set(prev).add(documentId))
      const result = await trpc.updateDocumentInverted.mutate({
        userId,
        documentId,
        inverted: !currentInverted,
      })
      if (result.success) {
        await fetchDocuments()
      }
    } catch (err) {
      console.error('Error updating document inverted status:', err)
    } finally {
      setUpdatingDocuments((prev) => {
        const newSet = new Set(prev)
        newSet.delete(documentId)
        return newSet
      })
    }
  }

  const handleDownloadDocument = async (
    documentId: string,
    fileName: string,
  ) => {
    try {
      const result = await trpc.downloadDocument.mutate({ documentId })
      if (result.success && result.fileBase64) {
        const byteCharacters = atob(result.fileBase64)
        const byteNumbers = new Array(byteCharacters.length)
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i)
        }
        const byteArray = new Uint8Array(byteNumbers)
        const blob = new Blob([byteArray], { type: 'application/pdf' })

        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = fileName
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      }
    } catch (error) {
      console.error('Error downloading document:', error)
    }
  }

  // Selection handlers
  const handleSelectDocument = (documentId: string) => {
    setSelectedDocuments((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(documentId)) {
        newSet.delete(documentId)
      } else {
        newSet.add(documentId)
      }
      return newSet
    })
  }

  const handleSelectAll = () => {
    if (selectedDocuments.size === documents.length) {
      setSelectedDocuments(new Set())
    } else {
      setSelectedDocuments(new Set(documents.map((doc) => doc.id)))
    }
  }

  // Loading state
  if (isLoading) {
    return (
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Box display="flex" justifyContent="center" alignItems="center" py={4}>
          <CircularProgress />
        </Box>
      </Container>
    )
  }

  // Error state
  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Alert severity="error">
          Evraklar yüklenirken hata oluştu: {error}
        </Alert>
      </Container>
    )
  }

  return (
    <Container maxWidth={CONTAINER_MAX_WIDTH} sx={{ py: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
            Evrak Yönetimi
          </Typography>
        </Box>
        <Typography variant="h6" color="text.secondary">
          Takımdaki tüm evrakları görüntüleyin ve yönetin
        </Typography>
      </Box>

      {/* Bulk Actions */}
      {selectedDocuments.size > 0 && (
        <BulkActionsToolbar
          selectedCount={selectedDocuments.size}
          onBulkInvertedUpdate={handleBulkInvertedUpdate}
          onBulkDelete={() => setBulkDeleteDialogOpen(true)}
          onClearSelection={() => setSelectedDocuments(new Set())}
        />
      )}

      {/* Documents Table */}
      <DocumentsTable
        documents={documents}
        members={members}
        selectedDocuments={selectedDocuments}
        updatingDocuments={updatingDocuments}
        onSelectDocument={handleSelectDocument}
        onSelectAll={handleSelectAll}
        onDownloadDocument={handleDownloadDocument}
        onDeleteDocument={(documentId: string) => {
          setDocumentToDelete(documentId)
          setDeleteDialogOpen(true)
        }}
        onInvertedToggle={handleInvertedToggle}
      />

      {/* Summary */}
      <DocumentsSummary
        documents={documents}
        selectedCount={selectedDocuments.size}
      />

      {/* Delete Dialogs */}
      <DocumentsDeleteDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={() => {
          if (documentToDelete) {
            handleDeleteDocument(documentToDelete)
          }
        }}
        title="Evrakı Sil"
        message="Bu evrakı silmek istediğinizden emin misiniz? Bu işlem geri alınamaz."
      />

      <DocumentsDeleteDialog
        open={bulkDeleteDialogOpen}
        onClose={() => setBulkDeleteDialogOpen(false)}
        onConfirm={handleBulkDelete}
        title="Seçili Evrakları Sil"
        message={`${selectedDocuments.size} evrakı silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.`}
      />
    </Container>
  )
}
